import {
  editorPagesAtom,
  editorPageSetTurnedOverPromptAtom,
  alignmentPointsAtom,
  alignmentVisibilityAtom,
  isEditorReadyAtom,
  isSavingAtom,
  unsavedChangesAtom,
} from '@/atoms/_editorAtoms'
import {
  editorRemintedItemIdsAtom,
  editorSelfDeletedPageIdsAtom,
  editorTextBoxDraftsAtom,
  editorTextBoxLocksAtom,
  editorTextBoxPendingDeletesAtom,
  editorTextBoxSeedIdsAtom,
} from '@/atoms/editorTextBoxAtoms'
import { showEditorTextBoxConvertModalForItemIdAtom } from '@/atoms/itemControlAtoms'
import {
  itemIdAtom,
  selectionRangeAtom,
  pageZoomAtom,
  selectedPageAtom,
  selectedItemsAtom,
  orderAtom,
  editorLayoutAtom,
  pageLayoutAtom,
  themeOverrideAtom,
  openSwapImageMenuAtom,
} from 'atoms'
import { useResetRecoilState, useRecoilValue } from 'recoil'
import { useDeleteItems } from './'
import { dropEditorSaveLatch } from '@/utils/editor/editorSaveSession'

export const useResetEditorAtoms = () => {
  const itemIds = useRecoilValue(itemIdAtom)
  const deleteItem = useDeleteItems()

  const resetEditorPages = useResetRecoilState(editorPagesAtom)
  const resetIsEditorReady = useResetRecoilState(isEditorReadyAtom)
  const resetIsSaving = useResetRecoilState(isSavingAtom)
  const resetUnsavedChanges = useResetRecoilState(unsavedChangesAtom)
  const resetTurnedOverPrompt = useResetRecoilState(
    editorPageSetTurnedOverPromptAtom,
  )
  const resetOrder = useResetRecoilState(orderAtom)
  const resetItemIds = useResetRecoilState(itemIdAtom)
  const resetSelectionRangeAtom = useResetRecoilState(selectionRangeAtom)
  const resetPageZoomAtom = useResetRecoilState(pageZoomAtom)
  const resetSelectedItemsAtom = useResetRecoilState(selectedItemsAtom)
  const resetAlignmentPointsAtom = useResetRecoilState(alignmentPointsAtom)
  const resetAlignmentVisibilityAtom = useResetRecoilState(
    alignmentVisibilityAtom,
  )
  const resetSelectedPageAtom = useResetRecoilState(selectedPageAtom)
  const resetEditorLayoutAtom = useResetRecoilState(editorLayoutAtom)
  const resetPageLayout = useResetRecoilState(pageLayoutAtom)
  const resetThemeOverrideAtom = useResetRecoilState(themeOverrideAtom)
  const resetSwapImageMenu = useResetRecoilState(openSwapImageMenuAtom)
  const resetEditorTextBoxDrafts = useResetRecoilState(editorTextBoxDraftsAtom)
  const resetEditorTextBoxPendingDeletes = useResetRecoilState(
    editorTextBoxPendingDeletesAtom,
  )
  const resetEditorTextBoxLocks = useResetRecoilState(editorTextBoxLocksAtom)
  const resetEditorTextBoxSeedIds = useResetRecoilState(
    editorTextBoxSeedIdsAtom,
  )
  const resetEditorSelfDeletedPageIds = useResetRecoilState(
    editorSelfDeletedPageIdsAtom,
  )
  const resetEditorRemintedItemIds = useResetRecoilState(
    editorRemintedItemIdsAtom,
  )
  const resetEditorTextBoxConvertConfirmation = useResetRecoilState(
    showEditorTextBoxConvertModalForItemIdAtom,
  )

  return () => {
    itemIds?.map((id) => {
      deleteItem(id)
      return id
    })
    resetEditorPages()
    // Re-init clears the working copy; drop the ready flag so the save guard
    // treats the un-hydrated window as a potential wipe until Page.js re-hydrates.
    resetIsEditorReady()
    // isSavingAtom persists across editor remounts (app-level RecoilRoot); an
    // abandoned mid-save exit would otherwise re-enter a pre-locked editor.
    // The latch in front of it is module state and outlives the tree too, so
    // it is dropped here for the same reason. The abandoned save's own release
    // then does nothing, because the token it holds is no longer the latch's.
    resetIsSaving()
    dropEditorSaveLatch()
    // The flag outlives the editor too (app-level RecoilRoot). Every load
    // starts from the server's pages, so nothing it marked survives one, and
    // a page left unsaved through Exit must not open marked in the same tab.
    resetUnsavedChanges()
    resetItemIds()
    resetSelectionRangeAtom()
    resetPageZoomAtom()
    resetSelectedItemsAtom()
    resetAlignmentPointsAtom()
    resetAlignmentVisibilityAtom()
    resetSelectedPageAtom()
    resetOrder()
    resetEditorLayoutAtom()
    resetPageLayout()
    resetThemeOverrideAtom()
    resetSwapImageMenu()
    resetEditorTextBoxDrafts()
    resetEditorTextBoxPendingDeletes()
    // The lock is session state; a flip never outlives the editor it was
    // made in.
    resetEditorTextBoxLocks()
    // A seed whose first paint never ran is nobody's seed once the editor
    // closes: its row opens at its saved frame like any other.
    resetEditorTextBoxSeedIds()
    // Deletes this tab made are finished business once it reloads.
    resetEditorSelfDeletedPageIds()
    // The next load mints its own ids and records them afresh.
    resetEditorRemintedItemIds()
    // A conversion left unconfirmed is not asked again on the next load.
    resetEditorTextBoxConvertConfirmation()
    // The prompt belongs to the pages this tab loaded; a new load is not stale.
    resetTurnedOverPrompt()
  }
}

export default useResetEditorAtoms
